import * as Yup from "yup";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import Typography from "@material-ui/core/Typography";
import React, { ChangeEvent, FC, useState } from "react";
import { Auth } from "aws-amplify";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import AppLink from "../../../../components/elements/AppLink";
import BaseInput from "../../../../components/elements/BaseInput";
import Button from "../../../../components/elements/Button";
import FormError from "../../../../components/elements/FormError";
import FormFieldError from "../../../../components/elements/FormFieldError";
import classes from "./SignInForm.module.scss";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import { setIsAuthLoading } from "../../../../redux/reducers/session";

type SignInForm = {
  setFirstAttempt: (user: any) => void;
  setAuthUser: (isFirstAttempt: boolean) => void;
};

type FormInitialValues = {
  userName: string;
  password: string;
};

const SignInForm: FC<SignInForm> = ({ setFirstAttempt, setAuthUser }) => {
  const history = useHistory();

  const dispatch = useDispatch();

  const formInitialValues: FormInitialValues = {
    userName: "",
    password: "",
  };

  const validationSchema = Yup.object({
    userName: Yup.string().required("Required field."),
    password: Yup.string().required("Required field."),
  });

  const [authErrorMessage, setAuthErrorMessage] = useState<string>("");

  const isAuthLoading = useSelector((state: ReduxApplicationState) => state.session.isLoading);

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      const { userName, password } = values;
      dispatch(setIsAuthLoading(true));
      const user = await Auth.signIn(userName, password);
      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        setAuthUser(user);
        setFirstAttempt(true);
        return;
      }
      actions.resetForm();
      history.push("/dashboard");
    } catch (error) {
      const err = error as any;
      dispatch(setIsAuthLoading(false));
      actions.resetForm();
      setAuthErrorMessage(`There was an error logging in: ${err.message}`);
    }
  };

  const handleFieldChange =
    (formikHandler: (e: string | ChangeEvent<any>) => void) => (e: string | ChangeEvent<any>) => {
      formikHandler(e);
      if (authErrorMessage) {
        setAuthErrorMessage("");
      }
    };

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
    >
      {({ handleChange, values }) => (
        <Form className={classes.container}>
          <div className={classes.formContent}>
            <div className={classes.formHeader}>
              <Typography variant="h3">Log in</Typography>
              <AccountCircleIcon className={classes.icon} />
            </div>

            <div className={classes.inputs}>
              <div className={classes.input}>
                <Field
                  as={BaseInput}
                  value={values.userName}
                  error={!!authErrorMessage}
                  name="userName"
                  variant="outlined"
                  label="Username *"
                  onChange={handleFieldChange(handleChange("userName"))}
                />

                <FormFieldError name="userName" />
              </div>

              <div className={classes.input}>
                <Field
                  as={BaseInput}
                  value={values.password}
                  error={!!authErrorMessage}
                  name="password"
                  variant="outlined"
                  label="Password *"
                  type="password"
                  onChange={handleFieldChange(handleChange("password"))}
                />

                <FormFieldError name="password" />
                <FormError className={classes.formError} errorMessage={authErrorMessage} />
              </div>
            </div>

            <div className={classes.formControls}>
              <AppLink to="/request-password-reset">Forgot Password?</AppLink>
              <Button variant="contained" type="submit" isLoading={isAuthLoading} fullWidth>
                Log in
              </Button>
              <span>
                <AppLink to="/bgsignup">Don't have an account?</AppLink>{" "}
                <AppLink to="/bgsignup" highlighted>
                  Register
                </AppLink>
              </span>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default SignInForm;
